import { useCallback, useEffect, useState } from "react";
import { fetchKbQuestionDetail, toggleKbQuestionActive } from "../services/kbQuestionsService";
import type { KbQuestionDetailItem } from "../types/kbQuestion";

export type UseKbQuestionDetailResult = {
  readonly detail: KbQuestionDetailItem | null;
  readonly loading: boolean;
  readonly isTogglingStatus: boolean;
  readonly error: string | null;
  readonly reload: () => void;
  readonly toggleStatus: () => Promise<void>;
};

export const useKbQuestionDetail = (
  questionId: string | null
): UseKbQuestionDetailResult => {
  const [detail, setDetail] = useState<KbQuestionDetailItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => {
    setReloadToken((value) => value + 1);
  }, []);

  useEffect(() => {
    const normalizedId = questionId?.trim() ?? "";
    if (!normalizedId) {
      setDetail(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const response = await fetchKbQuestionDetail(normalizedId);
        if (cancelled) return;
        setDetail(response);
      } catch (e) {
        if (cancelled) return;
        const message = e instanceof Error ? e.message : "Failed to load KB question detail";
        setError(message);
        setDetail(null);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [questionId, reloadToken]);

  const toggleStatus = useCallback(async () => {
    const normalizedId = questionId?.trim() ?? "";
    if (!normalizedId || isTogglingStatus) return;

    setIsTogglingStatus(true);
    setError(null);

    try {
      const response = await toggleKbQuestionActive(normalizedId);
      setDetail((prev) => (prev
        ? {
            ...prev,
            is_active: response.is_active,
          }
        : prev));
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to update question status";
      setError(message);
    } finally {
      setIsTogglingStatus(false);
    }
  }, [questionId, isTogglingStatus]);

  return {
    detail,
    loading,
    isTogglingStatus,
    error,
    reload,
    toggleStatus,
  };
};
